import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, Clock, Crown, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CUR_SYMBOL = { EUR: "€", USD: "$", NGN: "₦", GBP: "£" };
const fmtPrice = (amount, cur) => {
  const sym = CUR_SYMBOL[cur] || `${cur} `;
  return cur === "NGN" ? `${sym}${Math.round(amount).toLocaleString()}` : `${sym}${Number(amount).toFixed(2)}`;
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export const SubscriptionPlans = ({ subscription, onChanged }) => {
  const [plans, setPlans] = useState(null);
  const [currency, setCurrency] = useState("EUR");
  const [country, setCountry] = useState("");
  const [busy, setBusy] = useState(null);
  const [params, setParams] = useSearchParams();
  const polled = useRef(false);

  const loadPlans = useCallback(() => {
    api.get("/seller/subscription-plans").then((r) => {
      setPlans(r.data.plans || []);
      setCurrency(r.data.currency || "EUR");
      setCountry(r.data.country || "");
    }).catch(() => setPlans([]));
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  // Handle return from Stripe/Paystack: poll subscription payment status.
  useEffect(() => {
    const sess = params.get("sub_session");
    if (!sess || polled.current) return;
    polled.current = true;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/seller/subscription/status/${sess}`);
        if (data.payment_status === "paid") {
          toast.success("Subscription activated 🎉");
          const next = new URLSearchParams(params); next.delete("sub_session"); setParams(next);
          onChanged?.();
          return;
        }
      } catch { /* ignore */ }
      if (tries < 8) setTimeout(tick, 1500);
      else {
        toast.message("Payment is processing — refresh in a moment to see your plan.");
        const next = new URLSearchParams(params); next.delete("sub_session"); setParams(next);
      }
    };
    tick();
  }, [params, setParams, onChanged]);

  const subscribe = async (duration) => {
    setBusy(duration);
    try {
      const { data } = await api.post("/seller/subscribe", { duration, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start subscription");
      setBusy(null);
    }
  };

  const sub = subscription || {};
  const onTrial = sub.on_trial;
  const paidActive = sub.paid_active;

  return (
    <div className="space-y-6">
      {(sub.store_paused || sub.in_grace) && (
        <Card data-testid="sub-lapse-alert" className={`p-4 flex items-start gap-3 ${sub.store_paused ? "bg-red-50 border-red-200 dark:bg-red-950/20" : "bg-amber-50 border-amber-200 dark:bg-amber-950/20"}`}>
          <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${sub.store_paused ? "text-red-600" : "text-amber-600"}`} />
          <div className="text-sm">
            {sub.store_paused ? (
              <p><strong className="text-red-700 dark:text-red-400">Your store is paused.</strong> Your subscription lapsed past the grace period, so your products are hidden from shoppers. Renew any plan below to go live again instantly — your data is safe.{sub.followers_waiting > 0 && <span className="block mt-1 font-medium text-foreground" data-testid="followers-waiting">🔔 {sub.followers_waiting} shopper{sub.followers_waiting !== 1 ? "s are" : " is"} waiting to be notified when you reopen.</span>}</p>
            ) : (
              <p><strong className="text-amber-700 dark:text-amber-400">Your subscription has lapsed.</strong> Your store stays live for a grace period of <strong>{sub.grace_days_left} more day{sub.grace_days_left !== 1 ? "s" : ""}</strong>. Renew now to avoid your store being paused.</p>
            )}
          </div>
        </Card>
      )}
      {/* Status banner */}
      <Card className="p-5 shadow-sm">
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${paidActive ? "bg-green-100 text-green-700" : onTrial ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-700"}`}>
              {paidActive ? <Crown className="w-5 h-5" /> : onTrial ? <Clock className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
            </span>
            <div>
              <p className="font-semibold" data-testid="sub-status-label">
                {paidActive ? `Active plan — ${sub.plan_months ? (sub.plan_months === 12 ? "1 year" : `${sub.plan_months} months`) : ""}` : onTrial ? "Free trial" : "No active subscription"}
              </p>
              <p className="text-sm text-muted-foreground">
                {paidActive
                  ? `Renews on ${fmtDate(sub.expires_at)} · ${sub.expires_days_left} days left`
                  : onTrial
                    ? `${sub.trial_days_left} days left on your free 3-month trial (0% commission during trial)`
                    : "Subscribe to keep your store active. A 5% commission also applies to each sale."}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="gap-1.5 text-sm py-1.5 px-3">
            <ShieldCheck className="w-4 h-4" /> Commission {sub.commission_pct != null ? sub.commission_pct : 5}%
          </Badge>
        </div>
      </Card>

      <div>
        <h3 className="font-display font-bold text-lg mb-1">Choose your plan</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Prices for <strong>{country || "your region"}</strong> in {currency}. You also pay a {sub.commission_pct != null ? sub.commission_pct : 5}% commission per sale.
        </p>
        {plans === null ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : plans.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">No plans configured yet. Please contact support.</Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((p) => {
              const best = p.duration === 12;
              return (
                <Card key={p.duration} data-testid={`plan-card-${p.duration}`}
                  className={`p-5 flex flex-col relative ${best ? "border-primary shadow-md ring-1 ring-primary/20" : "shadow-sm"}`}>
                  {best && <Badge className="absolute -top-2 right-3 bg-primary">Best value</Badge>}
                  <p className="text-sm font-semibold text-muted-foreground">{p.label}</p>
                  <p className="text-2xl font-black font-display mt-1" data-testid={`plan-price-${p.duration}`}>
                    {p.price > 0 ? fmtPrice(p.price, currency) : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 mb-4">
                    {p.price > 0 ? `${fmtPrice(p.price / p.duration, currency)} / month` : "Not available"}
                  </p>
                  <Button className="w-full rounded-full mt-auto" data-testid={`subscribe-btn-${p.duration}`}
                    disabled={busy != null || p.price <= 0} onClick={() => subscribe(p.duration)}>
                    {busy === p.duration ? <Loader2 className="w-4 h-4 animate-spin" /> : (paidActive ? "Renew / Extend" : "Subscribe")}
                  </Button>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
